// BrowserSessions — "Your browsers" in the Share tab (remote share phase 1, owner 2026-10-08).
// Lists the agent's own browsers running the extension and, per portal, which account each is
// logged into, so the agent knows before pressing a portal whether that browser can post there.
// Data: GET /api/core/ext-sessions (server/lib/ext_sessions.js), fed by the extension's
// portal_sessions.js heartbeat. Phase 2: when this page has no extension of its own (a phone),
// the Share buttons send a job to the SELECTED online browser — `selected`/`onSelect` below.
import { useEffect, useState } from 'react'
import { t } from '../../lib/i18n.js'
import { getExtSessions } from '../../db/core.js'

const PORTALS = [
  { key: 'ss',     label: 'SS.ge',  tone: 'text-red-500' },
  { key: 'myhome', label: 'MyHome', tone: 'text-emerald-500' },
  { key: 'korter', label: 'Korter', tone: 'text-amber-500' },
]
const POLL_MS = 30_000

function ago(iso) {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000)
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  return h < 48 ? `${h}h` : `${Math.round(h / 24)}d`
}

export function BrowserSessions({ selectable = false, selected = null, onSelect, onSessions }) {
  const [sessions, setSessions] = useState(null)   // null = loading; [] = none
  useEffect(() => {
    let dead = false
    const load = () => getExtSessions()
      .then(r => { if (!dead) { const l = r?.sessions || []; setSessions(l); onSessions?.(l) } })
      .catch(() => { if (!dead) setSessions(s => s ?? []) })   // an error must not wipe a shown list
    load()
    const id = setInterval(load, POLL_MS)
    return () => { dead = true; clearInterval(id) }
  }, [])   // eslint-disable-line react-hooks/exhaustive-deps -- onSessions is a setter

  if (sessions === null) return null
  return (
    <div className="mb-2 rounded border border-border px-3 py-2 text-sm">
      <p className="text-xs font-medium text-muted-foreground mb-1">{t('desk_share_browsers')}</p>
      {sessions.length === 0 && <p className="text-xs text-muted-foreground">{t('desk_share_browsers_none')}</p>}
      {sessions.map(s => (
        <div key={s.device_id} className={`py-1 ${s.online ? '' : 'opacity-50'}`}>
          <div className="flex items-center gap-2">
            {selectable && s.online && (
              <input type="radio" name="share-browser" aria-label={t('desk_share_browser_use')}
                checked={selected === s.device_id} onChange={() => onSelect?.(s.device_id)} />
            )}
            <span className={s.online ? 'text-emerald-500' : 'text-muted-foreground'}>●</span>
            <span className="font-medium">{s.device_name}</span>
            <span className="text-xs text-muted-foreground">
              {s.online ? t('desk_share_browser_online') : `${t('desk_share_browser_offline')} ${ago(s.last_seen)}`}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 pl-5 text-xs">
            {PORTALS.map(p => {
              const a = s.portals?.[p.key] || {}
              return (
                <span key={p.key}>
                  <span className={p.tone}>●</span> {p.label}:{' '}
                  {a.logged_in === true
                    ? <b className="text-foreground">{a.name || '✓'}{a.phone_tail ? ` …${a.phone_tail}` : ''}</b>
                    : <span className="text-muted-foreground">
                        {a.logged_in === false ? t('desk_share_portal_logged_out') : t('desk_share_portal_unknown')}
                      </span>}
                </span>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
